/**
 * Help flow — the definition the guided-flow engine runs when the kitchen presses "Ayuda".
 *
 * Why this exists (numbers from the Intercom review of 409 tablet conversations, 12 months to
 * 2026-09-08): since the composer started opening pre-filled, 48 % of the messages arrived with
 * NOTHING after the template's colon; support then needs a median of 9 messages to work out what
 * happened and in 27 % of conversations has to ask WHICH order it was — an id the tablet already
 * knows. Six one-tap topics cover 55 % of everything the kitchen writes; free text stays for the
 * rest. The Spanish labels are the kitchen's own wording, lifted from the messages.
 *
 * Three jobs, all pure so they are unit-tested against the real i18n catalogue:
 *   1. HELP_TOPICS — the ranked list (volume order, do not reorder by "importance"), each with
 *      the i18n keys the modal and the message use, whether it is ABOUT an order (`subject`), and
 *      whether the app should first try to fix it on its own (`remedy`).
 *   2. snapshotHelpContext / describeOrder / helpCandidates — turn App.vue state into a plain
 *      context: what is on screen, which order is open, how the camera and the connection are.
 *   3. composeHelpMessage — the support message that goes to support: the kitchen's sentence, the
 *      order it is about, what the app already tried, and a technical line support would otherwise
 *      have to ask for. It reaches Intercom one of two ways, decided by App.vue: pre-filled in the
 *      composer for the kitchen to complete and send, or sent outright with `startConversation`
 *      when the direct-send switch is on. Free text always goes through the composer.
 */

import { isDecidedAnalysis, isIncorrectAnalysis } from './verdictState.js';

export const HELP_FLOW_ID = 'help';

export const HELP_REMEDY = Object.freeze({
  RELOAD: 'reload', // full page reload — re-runs Amplify config, auth, the fetch chain and the camera
});

/** Max orders offered on the "which order?" step — a glove-friendly list, not a directory. */
export const HELP_PICK_MAX = 8;

// Ranked by 12-month volume (see docs/flujo-ayuda-guiado.md).
//
// `iconPath` is a filled 24-grid silhouette, not a line icon: at the distance the tablet is read,
// PrimeIcons' 1.5/16 em stroke works out to ~0.6 mm, right at the eye's resolution limit and the
// first thing a reflection erases. A solid mass survives both. The seven shapes are deliberately
// unlike each other — a notched ticket, a bag, a camera, stacked frames, the only round one, a
// scooter, a speech bubble — because with one single colour the silhouette is what tells them
// apart. `tone` is kept for other flows; the help grid no longer tints anything.
export const HELP_TOPICS = Object.freeze([
  {
    id: 'missing_order',
    subject: 'platform',
    // Después del reinicio, no antes: el reinicio arregla la mayoría, así que solo se pregunta la
    // agregadora a quien de verdad va a acabar escribiendo. Así soporte puede ir directo a mirar
    // el canal correcto (Glovo, Uber…) en vez de preguntarlo.
    subjectWhen: 'escalate',
    iconPath: 'M19.5 3.5 18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5ZM19 19.09H5V4.91h14v14.18ZM6 15h12v2H6Zm0-4h12v2H6Zm0-4h12v2H6Z',
    icon: 'pi-inbox',
    tone: 'amber',
    remedy: HELP_REMEDY.RELOAD,
    labelKey: 'message.helpFlowTopicMissingOrder',
    bodyKey: 'message.helpFlowMsgMissingOrder',
    confirmKey: 'message.helpFlowConfirmMissingOrder',
  },
  {
    id: 'item_flagged_present',
    // El pedido del que se habla casi siempre ha salido ya de la pantalla (la bolsa se entregó y la
    // cocina lo cuenta después), así que se elige de los últimos fotografiados, del servidor —
    // igual que "ver las fotos" — y no de lo que queda en pantalla, que dejaba a soporte sin código.
    // Si no está, se pregunta el código. Los que la IA marcó como incorrectos van primero.
    fetch: 'photos',
    askCode: true,
    incorrectFirst: true,
    pickTitleKey: 'message.helpFlowPickFlaggedTitle',
    iconPath: 'M18 6h-2c0-2.21-1.79-4-4-4S8 3.79 8 6H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2Zm-6-2a2 2 0 0 1 2 2h-4a2 2 0 0 1 2-2Z',
    icon: 'pi-shopping-bag',
    tone: 'red',
    subject: 'order',
    remedy: null,
    labelKey: 'message.helpFlowTopicItemFlaggedPresent',
    bodyKey: 'message.helpFlowMsgItemFlaggedPresent',
    confirmKey: null,
  },
  {
    id: 'cannot_take_photo',
    iconPath: 'M9 2 7.2 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-3.2L15 2H9Zm3 15a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2.2a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6Z',
    icon: 'pi-camera',
    tone: 'blue',
    subject: null,
    remedy: HELP_REMEDY.RELOAD,
    labelKey: 'message.helpFlowTopicCannotTakePhoto',
    bodyKey: 'message.helpFlowMsgCannotTakePhoto',
    confirmKey: 'message.helpFlowConfirmCamera',
  },
  {
    id: 'see_order_photos',
    iconPath: 'M22 16V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2Zm-11-4 2.03 2.71L16 11l4 5H8l3-4ZM2 6v14a2 2 0 0 0 2 2h14v-2H4V6H2Z',
    icon: 'pi-images',
    tone: 'slate',
    subject: 'order',
    // Se contesta enseñando las fotos (src/orderPhotos.js), no escribiendo a soporte: era la
    // petición que más se repetía y soporte solo podía ir al Hub a hacer una captura. Soporte
    // sigue a un toque desde el visor, y "No está en la lista" le escribe como antes. Con el
    // interruptor `help-order-photos` a false, App.vue le quita `view` y vuelve al tique (con el
    // pedido elegido de la misma lista, o su código).
    view: true,
    fetch: 'photos',
    askCode: true,
    pickTitleKey: 'message.helpPhotosPickTitle',
    remedy: null,
    labelKey: 'message.helpFlowTopicSeeOrderPhotos',
    bodyKey: 'message.helpFlowMsgSeeOrderPhotos',
    // Desde el visor ya las han visto: pedirlas otra vez sería mentir sobre lo que pasa.
    viewedBodyKey: 'message.helpFlowMsgSeeOrderPhotosViewed',
    confirmKey: null,
  },
  {
    id: 'unexpected_orders',
    subject: 'order',
    multi: true,
    iconPath: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2ZM4 12c0-4.42 3.58-8 8-8 1.85 0 3.55.63 4.9 1.69L5.69 16.9A7.9 7.9 0 0 1 4 12Zm8 8c-1.85 0-3.55-.63-4.9-1.69L18.31 7.1A7.9 7.9 0 0 1 20 12c0 4.42-3.58 8-8 8Z',
    icon: 'pi-clone',
    tone: 'amber',
    remedy: null,
    labelKey: 'message.helpFlowTopicUnexpectedOrders',
    bodyKey: 'message.helpFlowMsgUnexpectedOrders',
    confirmKey: null,
  },
  {
    id: 'left_without_photo',
    // El pedido que salió sin foto ya no está en pantalla: se elige de los últimos SIN foto, del
    // servidor, y si no está se pregunta el código.
    fetch: 'no_photo',
    askCode: true,
    pickTitleKey: 'message.helpFlowPickNoPhotoTitle',
    iconPath: 'M19 7c0-1.1-.9-2-2-2h-3v2h3v2.65L13.52 14H10V9H6c-2.21 0-4 1.79-4 4v3h2c0 1.66 1.34 3 3 3s3-1.34 3-3h4.48L19 10.35V7ZM7 17c-.55 0-1-.45-1-1h2c0 .55-.45 1-1 1Zm10-2c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3Zm0 4c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1Z',
    icon: 'pi-truck',
    tone: 'slate',
    subject: 'order',
    remedy: null,
    labelKey: 'message.helpFlowTopicLeftWithoutPhoto',
    bodyKey: 'message.helpFlowMsgLeftWithoutPhoto',
    confirmKey: null,
  },
  {
    // Free text. Keeps the legacy template (`message.helpPrefill`) so the kitchen completes the
    // colon as before — 11 % of conversations fit no category and some of them are real faults.
    id: 'other',
    iconPath: 'M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z',
    icon: 'pi-comments',
    tone: 'ghost',
    subject: null,
    remedy: null,
    free: true,
    labelKey: 'message.helpFlowTopicOther',
    bodyKey: null,
    confirmKey: null,
  },
  {
    // Contarlo hablando en vez de escribiendo, con las Voice notes nativas de Intercom (dictado:
    // Intercom transcribe y la persona revisa el texto antes de enviarlo). El mensaje con el
    // contexto de siempre sale primero y deja el redactor VACÍO, que es la única forma de que el
    // micrófono esté: con texto escrito, Intercom lo quita. Después App.vue pulsa el micro por la
    // persona (src/intercomComposer.js). Es una salida, como "Otra cosa", no un tema.
    id: 'voice_note',
    iconPath: 'M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z',
    icon: 'pi-microphone',
    tone: 'ghost',
    subject: null,
    remedy: null,
    voice: true,
    labelKey: 'message.helpFlowTopicVoiceNote',
    bodyKey: 'message.helpFlowMsgVoiceNote',
    confirmKey: null,
  },
]);

export function findHelpTopic(id) {
  return HELP_TOPICS.find((topic) => topic.id === id) || null;
}

/**
 * Where the help button was pressed from. The list is not the same problem space in each place:
 * with an order open in the camera, "an order is missing from the screen" is not what is going on.
 */
export const HELP_SURFACE = Object.freeze({
  ORDERS: 'orders',   // the order list — the kitchen is looking at what arrived
  ORDER: 'order',     // an order is open in the camera — they are photographing it
  OTHER: 'other',
});

// Por superficie, qué temas suben al principio. Lo que no se nombra conserva el orden de volumen,
// que es el ranking de doce meses de conversaciones. "Otra cosa" siempre al final: es la salida.
const SURFACE_PRIORITY = Object.freeze({
  [HELP_SURFACE.ORDER]: ['item_flagged_present', 'cannot_take_photo', 'see_order_photos'],
  [HELP_SURFACE.ORDERS]: ['missing_order', 'unexpected_orders'],
});

/**
 * The topic list reordered for where the person is standing. Pure and stable: an unlisted topic
 * keeps its volume rank, so the order is never arbitrary — it is the ranking with the topics that
 * cannot apply here pushed down.
 */
export function orderHelpTopics(topics, surface) {
  const priority = SURFACE_PRIORITY[surface] || [];
  const rank = (topic) => {
    if (topic.free) return 1000; // las salidas, siempre al final: escribirlo…
    if (topic.voice) return 1001; // …o contarlo en una nota de voz
    const i = priority.indexOf(topic.id);
    return i === -1 ? 100 + topics.indexOf(topic) : i;
  };
  return [...topics].sort((a, b) => rank(a) - rank(b));
}

/**
 * Is the Intercom messenger REALLY able to open the composer? `Boolean(window.Intercom)` is
 * not that test: the npm loader installs a queue holder — a function carrying a `.q` array —
 * the moment the SDK initialises, BEFORE the widget script loads from widget.intercom.io. With
 * that host blocked or the tablet offline, every command is queued in silence: the tap lands
 * nowhere, which is exactly the dead button this flow exists to remove. The real widget
 * replaces the holder (no `.q`) and flags itself with `booted`.
 * @param {*} ic  window.Intercom (injected for tests)
 */
export function isMessengerReady(ic) {
  if (typeof ic !== 'function') return false;
  if (ic.booted === true) return true;
  if (Array.isArray(ic.q)) return false; // still the loader's queue: the widget never arrived
  return true;
}

// ---- context ---------------------------------------------------------------------------------

/**
 * The analysis on an order row. The server sends `order.orderAnalyses` as an ARRAY; App.vue's
 * fetch path flattens it to ONE object (`{ id, isCorrect, appResponse }`, with `isCorrect: null`
 * when nothing has run yet). Rows can carry either shape depending on which path produced them.
 */
function analysisOf(order) {
  const analyses = order.order?.orderAnalyses;
  if (Array.isArray(analyses)) return analyses[0] || null;
  return analyses || null;
}

/**
 * A plain, serialisable description of one order — the fields support needs and nothing else.
 * Works on App.vue's processedOrders rows and on CameraScreen's local copies alike, and derives
 * the verdict the way the order tile does (OrderSelectionComponent.getOrderClass), so the badge on
 * the pick step matches the colour the kitchen is looking at.
 * @param {object} order
 * @param {(platform: string) => string} [platformLabel]  host's human platform name
 * @param {object} [opts]
 * @param {boolean} [opts.aiOff]  restaurant.proActivated === false: a photographed order is done
 */
export function describeOrder(order, platformLabel = null, { aiOff = false } = {}) {
  if (!order) return null;
  const analysis = analysisOf(order);
  const photoCount = order.photoCount ?? order.order?.photos?.length ?? 0;
  // `manualIsCorrect` lives on the inner order (the server's shape); the top level is only a
  // courtesy for callers that flattened it.
  const manualOk = order.order?.manualIsCorrect === true || order.manualIsCorrect === true;
  const analysisOk = analysis?.appResponse
    ? (analysis.isCorrect === true && analysis.appResponse === 'ORDER_ANALYZED') || analysis.appResponse === 'EMPTY_ORDER_CONTENT'
    : analysis?.isCorrect === true;
  const isCorrect = manualOk || analysisOk || order.analysisResult?.isCorrect === true || (aiOff && photoCount > 0);
  // `isCorrect === false` alone is not a verdict: the CV inserts the analysis row with the column
  // default before any comparison, and processOrders copies that false into `analysisResult`. It
  // counts only once decided (verdictState.js); a photographed order without one is "pending".
  // `analysisResult` alone (no analysis row) is a verdict this tablet produced itself.
  const analysisResultWrong = order.analysisResult?.isCorrect === false
    && (isDecidedAnalysis(order.analysisResult) || !analysis);
  const isIncorrect = !isCorrect && (
    isIncorrectAnalysis(analysis)
    || analysis?.appResponse === 'EMPTY_MODEL_DETECTIONS'
    || analysisResultWrong
  );
  const platform = order.deliveryPlatform || null;
  return {
    kind: 'order',
    orderId: order.orderId,
    // Same rule as the tile the kitchen is looking at (CameraScreen.orderDisplayLabel), so the
    // code in the ticket matches the code on the screen.
    code: order.ticketId || order.deliveryId || order.posId || order.orderId,
    platform,
    platformName: platform ? (platformLabel ? platformLabel(platform) : platform) : null,
    analysisId: order.orderAnalysis || analysis?.id || null,
    verdict: isCorrect ? 'correct' : isIncorrect ? 'incorrect' : photoCount > 0 ? 'pending' : 'none',
    photoCount,
    manual: Boolean(order._isManual),
  };
}

/**
 * A delivery platform as a pickable subject, for "an order is missing": support can go straight to
 * the right channel (Glovo, Uber…) instead of asking which one it was.
 */
export function describePlatform(platform, platformLabel = null) {
  if (!platform) return null;
  return {
    kind: 'platform',
    platform,
    platformName: platformLabel ? platformLabel(platform) : platform,
  };
}

/**
 * The orders offered on the PICK step, as descriptors. Orders already handed to a rider are
 * gone from the kitchen's screen and are left out. For the AI-wrong topic the red ones come
 * first — that is the order the person is almost certainly talking about.
 */
export function helpCandidates(orders, topic, { riderDelivered = {}, platformLabel = null, aiOff = false, max = HELP_PICK_MAX } = {}) {
  const visible = (orders || [])
    .filter((order) => order && order.orderId && !riderDelivered[order.orderId])
    .map((order) => describeOrder(order, platformLabel, { aiOff }));
  if (topic?.id === 'item_flagged_present') {
    const rank = { incorrect: 0, pending: 1, none: 2, correct: 3 };
    visible.sort((a, b) => rank[a.verdict] - rank[b.verdict]);
  }
  return visible.slice(0, max);
}

/**
 * Everything the message and the CONFIRM evidence draw from, computed once per hand-off.
 * @param {object} input
 * @param {string} input.store
 * @param {string} input.deviceId
 * @param {string} input.appVersion
 * @param {boolean} input.online            navigator/ledger connectivity
 * @param {boolean} input.socketDeaf        the Events socket is in a confirmed deaf episode
 * @param {object[]} input.orders           App.vue processedOrders
 * @param {string|null} input.openOrderId   the order open in the camera screen, if any
 * @param {object} input.riderDelivered     orderId -> ts of orders handed to a rider (hidden)
 * @param {object|null} input.camera        `{ ready, permission, cameras, fault, code, stream, resolution, selectedCameraId }`
 * @param {(platform: string) => string} [input.platformLabel]
 * @param {boolean} [input.aiOff]           restaurant.proActivated === false
 */
export function snapshotHelpContext({
  store, deviceId, appVersion, online, socketDeaf, orders, openOrderId, riderDelivered, camera, platformLabel, aiOff = false,
} = {}) {
  const described = (orders || [])
    .filter((order) => order && order.orderId && !(riderDelivered || {})[order.orderId])
    .map((order) => describeOrder(order, platformLabel, { aiOff }));
  return {
    store: store || '',
    deviceId: deviceId || null,
    appVersion: appVersion || null,
    connection: online === false ? 'offline' : socketDeaf ? 'deaf' : 'ok',
    ordersOnScreen: described.length,
    ordersWithoutPhoto: described.filter((d) => d.photoCount === 0 && d.verdict !== 'correct').length,
    manualOrders: described.filter((d) => d.manual).length,
    codes: described.slice(0, HELP_PICK_MAX).map((d) => d.code),
    openOrder: (openOrderId && described.find((d) => d.orderId === openOrderId)) || null,
    camera: camera ? {
      // `ready` es la verdad de campo (hay fotogramas en el vídeo); `permission` es un dato
      // aparte, porque un permiso concedido con la cámara ocupada por otra app sigue siendo fallo.
      ready: camera.ready === true,
      permission: camera.permission || 'unknown',
      cameras: Number(camera.cameras) || 0,
      fault: camera.fault || null,
      code: camera.code || null,
      width: Number(camera.stream?.width) || 0,
      height: Number(camera.stream?.height) || 0,
      resolution: camera.resolution ?? null,
      selectedCameraId: camera.selectedCameraId || null,
    } : null,
  };
}

// ---- the message ------------------------------------------------------------------------------

/**
 * The text the composer opens with. First the kitchen's sentence, then the order, then what the
 * app already tried, then a technical line. Line breaks between blocks so the kitchen can see
 * where their own words would go; every line is a full sentence in case a client flattens them.
 *
 * @param {object} args
 * @param {object} args.topic     a HELP_TOPICS entry
 * @param {object} args.ctx       from snapshotHelpContext
 * @param {object} [args.subject] describeOrder() of the order it is about
 * @param {object} [args.remedy]  `{ kind, fixed }` when a remedy ran before escalating
 * @param {boolean} [args.fromView] the person asked from the photos they were looking at
 * @param {Function} args.t       vue-i18n translate — `t(key)`, `t(key, named)`, `t(key, count)`
 */
export function composeHelpMessage({ topic, ctx, subject = null, remedy = null, text = null, fromView = false, t }) {
  const store = ctx?.store || '';
  if (!topic) return t('message.helpPrefill', { store });

  // Texto libre: lo que ha escrito la persona ES el problema, y debajo va todo lo que sabemos de
  // la app para que soporte pueda descartar causas sin preguntar nada.
  if (topic.free) {
    const typed = (text || '').trim();
    if (!typed) return t('message.helpPrefill', { store });
    return [
      `${t('message.helpFlowMsgIntro', { store })} ${typed}`,
      '—',
      ...techLines(ctx, t),
    ].join('\n');
  }
  if (!topic.bodyKey) return t('message.helpPrefill', { store });

  const bodyKey = fromView && topic.viewedBodyKey ? topic.viewedBodyKey : topic.bodyKey;
  const lines = [`${t('message.helpFlowMsgIntro', { store })} ${t(bodyKey)}`];
  lines.push(...subjectLines(subject, t));
  if (remedy && remedy.fixed === false && remedy.kind === HELP_REMEDY.RELOAD) {
    lines.push(t('message.helpFlowMsgRemedyReload'));
  }
  lines.push('—');
  lines.push(...techLines(ctx, t));
  // The camera line only when it is the topic or something is actually wrong with it — a
  // healthy camera is noise on an order ticket.
  if (ctx.camera && (topic.id === 'cannot_take_photo' || ctx.camera.fault)) {
    lines.push(cameraLine(ctx.camera, t));
  }
  // Los códigos de la pantalla son la evidencia de "falta uno": lo que NO está solo se entiende
  // viendo lo que sí. En "pedidos que no deberían estar" solo se listan si la persona no ha
  // marcado ninguno: si los marcó, esa lista es la respuesta y repetir la pantalla entera es el
  // ruido que la selección viene a sustituir.
  const listedSelection = Array.isArray(subject) && subject.length > 0;
  if (ctx.codes?.length && (topic.id === 'missing_order' || (topic.id === 'unexpected_orders' && !listedSelection))) {
    lines.push(codesLine(ctx, t));
  }
  return lines.join('\n');
}

// El sujeto puede ser un pedido, una LISTA de pedidos (los que no deberían estar) o una
// agregadora. Cada forma tiene su línea; ninguna se calla, porque el hueco es justo lo que
// soporte acaba preguntando.
function subjectLines(subject, t) {
  if (!subject) return [];
  if (Array.isArray(subject)) {
    const codes = subject.filter(Boolean).map((s) => s.code).filter(Boolean);
    if (!codes.length) return [];
    return [t('message.helpFlowMsgOrderList', { codes: codes.join(', '), count: codes.length })];
  }
  if (subject.kind === 'platform') {
    return [t('message.helpFlowMsgPlatform', { platform: subject.platformName || subject.platform })];
  }
  // Escrito en la tablet, no elegido de los pedidos: soporte tiene que saber que puede venir con
  // una errata y que no hay análisis ni fotos atados a él.
  if (subject.kind === 'code') {
    return [t('message.helpFlowMsgOrderTyped', { code: subject.code })];
  }
  return [orderLine(subject, t)];
}

// Lo que sabemos de la app, en una línea. Va en TODOS los mensajes, incluido el de texto libre:
// es lo que permite descartar causas sin preguntar.
function techLines(ctx, t) {
  return [[
    t('message.helpFlowMsgTech', { device: ctx.deviceId || '?', version: ctx.appVersion || '?' }),
    connectionLabel(ctx.connection, t),
    t('message.helpFlowMsgOrdersOnScreen', ctx.ordersOnScreen),
    t('message.helpFlowMsgWithoutPhoto', ctx.ordersWithoutPhoto),
  ].join(' · ')];
}

function orderLine(subject, t) {
  const parts = [t('message.helpFlowMsgOrder', { code: subject.code })];
  if (subject.platformName) parts.push(subject.platformName);
  if (subject.manual) parts.push(t('message.helpFlowMsgManualOrder'));
  if (subject.analysisId) parts.push(t('message.helpFlowMsgAnalysis', { id: subject.analysisId }));
  return parts.join(' · ');
}

function connectionLabel(connection, t) {
  if (connection === 'offline') return t('message.helpFlowMsgConnOffline');
  if (connection === 'deaf') return t('message.helpFlowMsgConnDeaf');
  return t('message.helpFlowMsgConnOk');
}

// El orden importa y es el que evita el tique engañoso: primero el fallo diagnosticado, luego la
// evidencia de que la cámara está dando imagen (que gana a cualquier lectura de permiso), y solo
// si no hay imagen se habla del permiso — y distinguiendo "denegado" de "sin responder", porque
// son dos arreglos distintos en la tablet.
function cameraLine(camera, t) {
  const parts = [];
  if (camera.fault) {
    parts.push(t('message.helpFlowMsgCameraFault', { code: camera.code || camera.fault }));
  } else if (camera.ready) {
    parts.push(camera.width > 0
      ? t('message.helpFlowMsgCameraOk', { width: camera.width, height: camera.height })
      : t('message.helpFlowMsgCameraRunning'));
  } else if (camera.permission === 'denied') {
    parts.push(t('message.helpFlowMsgCameraNoPermission'));
  } else if (camera.permission === 'prompt') {
    parts.push(t('message.helpFlowMsgCameraPermissionPending'));
  } else {
    parts.push(t('message.helpFlowMsgCameraStarting'));
  }
  parts.push(t('message.helpFlowMsgCameraCount', camera.cameras));
  if (camera.resolution) parts.push(t('message.helpFlowMsgCameraResolution', { resolution: camera.resolution }));
  return parts.join(' · ');
}

function codesLine(ctx, t) {
  const line = t('message.helpFlowMsgCodes', { codes: ctx.codes.join(', ') });
  return ctx.manualOrders > 0 ? `${line} (${t('message.helpFlowMsgManualCount', ctx.manualOrders)})` : line;
}

// ---- the confirm step -------------------------------------------------------------------------

/**
 * The one fact shown under "did that fix it?", so the person answers about what the tablet
 * actually shows now rather than from memory. Null when there is nothing worth saying.
 */
export function helpEvidence(topic, ctx, t) {
  if (!topic || !ctx) return null;
  if (topic.id === 'cannot_take_photo') {
    if (ctx.camera?.fault) return t('message.helpFlowEvidenceCameraFault', { code: ctx.camera.code || ctx.camera.fault });
    if (ctx.camera?.ready) return t('message.helpFlowEvidenceCameraOk');
    return null;
  }
  return t('message.helpFlowEvidenceOrders', ctx.ordersOnScreen);
}
